import { useCallback, useEffect, useRef } from 'react';
import { assoc, assocPath, chain, dissoc, equals, filter, forEachObjIndexed, head, identical, includes, is, mergeDeepRight, mergeRight, path, prepend, prop, propEq, startsWith } from 'ramda';
import { isArray, isNotNilOrEmpty, lengthEq, notEqual } from 'ramda-adjunct';
import { compressImage, SUPPORTED_MIMETYPES } from '../lib/compressImage';
import { errorMessage } from '../utils/messageMutation';

const isDataUrl = (value) => is(String, value) && startsWith('data:', value);
const isBlob = (value) => typeof Blob !== 'undefined' && value instanceof Blob;

const URL_PENDING = 'pending';
const URL_TRACKED = 'tracked';

const getBackgroundImage = (frameStyle) => prop('backgroundImage', frameStyle);

const isPendingCompression = is(Promise);

// History snapshots must never hold a pending compression: an undo/redo would otherwise restore
// a Promise whose image the user never saw applied, and a save would upload it.
// settledUploadImageRef holds the last uploadImage that isn't a pending compression.
const settleUploadImage = (settledUploadImageRef, frameStyle) => {
  const uploadImage = prop('uploadImage', frameStyle);
  if (isPendingCompression(uploadImage)) return assoc('uploadImage', settledUploadImageRef.current, frameStyle);
  settledUploadImageRef.current = uploadImage;
  return frameStyle;
};

// objectUrlsRef holds the blob: URLs created by the hook: { [url]: URL_PENDING | URL_TRACKED }.
const revokeObjectUrl = (objectUrlsRef, url) => {
  URL.revokeObjectURL(url);
  objectUrlsRef.current = dissoc(url, objectUrlsRef.current);
};

const revokeAllObjectUrls = (objectUrlsRef) => {
  forEachObjIndexed((_, url) => URL.revokeObjectURL(url), objectUrlsRef.current);
  objectUrlsRef.current = {};
};

const getReferencedUrls = (frameStyle, historyActions = []) => prepend(
  getBackgroundImage(frameStyle),
  chain(
    (action) => [getBackgroundImage(prop('previousValue', action)), getBackgroundImage(prop('newValue', action))],
    filter(propEq('updateFrameStyle', 'type'), historyActions)
  )
);

const useFrameStyle = ({ setState, addToHistory, frameStyle, historyActions }) => {
  const addToHistoryRef = useRef(addToHistory);
  addToHistoryRef.current = addToHistory;

  const objectUrlsRef = useRef({});
  const settledUploadImageRef = useRef(undefined);
  const uploadTokenRef = useRef(0);
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      revokeAllObjectUrls(objectUrlsRef);
    };
  }, []);

  // Only the current state and the undo/redo snapshots still in the (bounded) history can
  // display a preview: the other ones are revoked.
  useEffect(() => {
    const referencedUrls = getReferencedUrls(frameStyle, historyActions);
    forEachObjIndexed((status, url) => {
      if (includes(url, referencedUrls)) {
        objectUrlsRef.current = assoc(url, URL_TRACKED, objectUrlsRef.current);
      } else if (!equals(URL_PENDING, status)) {
        revokeObjectUrl(objectUrlsRef, url);
      }
    }, objectUrlsRef.current);
  }, [frameStyle, historyActions]);

  const applyFrameStyle = useCallback((property, value) => {
    setState((prevState) => {
      const prevFrameStyle = path(['values', 'frameStyle'], prevState);

      const propertyPath = isArray(property) ? property : [property];
      const filteredPropertyPath = filter(isNotNilOrEmpty, propertyPath);
      const nextFrameStyle = isNotNilOrEmpty(filteredPropertyPath)
        ? assocPath(filteredPropertyPath, value, prevFrameStyle)
        : mergeDeepRight(prevFrameStyle, value);

      addToHistoryRef.current(
        settleUploadImage(settledUploadImageRef, prevFrameStyle),
        settleUploadImage(settledUploadImageRef, nextFrameStyle),
        'updateFrameStyle'
      );

      return assocPath(['values', 'frameStyle'], nextFrameStyle, prevState);
    });
  }, [setState]);

  const loadBackgroundImage = useCallback(async (file, token) => {
    const pendingCompression = compressImage(file);
    let previousUploadImage;
    // The pending compression is stored right away (without history entry) so that a save
    // happening meanwhile awaits it and still uploads the image.
    setState((prevState) => {
      // When another compression is still pending, the previous value is the last settled image.
      const prevFrameStyle = settleUploadImage(settledUploadImageRef, path(['values', 'frameStyle'], prevState));
      previousUploadImage = prop('uploadImage', prevFrameStyle);
      return assocPath(['values', 'frameStyle', 'uploadImage'], pendingCompression, prevState);
    });

    const compressedFile = await pendingCompression;
    if (!isMountedRef.current || notEqual(token, uploadTokenRef.current)) return;

    const previewUrl = URL.createObjectURL(compressedFile);
    objectUrlsRef.current = assoc(previewUrl, URL_PENDING, objectUrlsRef.current);
    setState((prevState) => {
      const prevFrameStyle = path(['values', 'frameStyle'], prevState);
      // Already uploaded by a save made during the compression.
      if (!identical(pendingCompression, prop('uploadImage', prevFrameStyle))) {
        revokeObjectUrl(objectUrlsRef, previewUrl);
        return prevState;
      }

      // The compressed file is the one uploaded on save (without being compressed again).
      const nextFrameStyle = mergeRight(prevFrameStyle, { uploadImage: compressedFile, backgroundImage: previewUrl });
      addToHistoryRef.current(
        assoc('uploadImage', previousUploadImage, prevFrameStyle),
        settleUploadImage(settledUploadImageRef, nextFrameStyle),
        'updateFrameStyle'
      );

      return assocPath(['values', 'frameStyle'], nextFrameStyle, prevState);
    });
  }, [setState]);

  const updateFrameStyle = useCallback((property, value) => {
    const propertyPath = isArray(property) ? property : [property];
    const key = lengthEq(1, propertyPath) ? head(propertyPath) : null;

    if (equals('uploadImage', key)) {
      // Other formats (e.g. HEIC) can't be displayed by most browsers nor uploaded: the current image is kept.
      if (isBlob(value) && !includes(prop('type', value), SUPPORTED_MIMETYPES)) {
        errorMessage('Format d\'image non supporté, veuillez utiliser une image JPEG ou PNG.');
        return;
      }
      // Any change of the uploaded image (new file or reset) invalidates a pending compression.
      uploadTokenRef.current += 1;
      if (isBlob(value)) {
        loadBackgroundImage(value, uploadTokenRef.current);
        return;
      }
    }
    if (equals('backgroundImage', key) && isDataUrl(value)) return;

    applyFrameStyle(property, value);
  }, [applyFrameStyle, loadBackgroundImage]);

  return { updateFrameStyle };
};

export default useFrameStyle;
